import React from 'react';
import { QueryHookOptions } from '@apollo/client';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import { useApolloClient, useQuery } from '@ts-gql/apollo';

export const GET_CLIENT_QUERY = gql`
  query GetClient($cloudId: String!) {
    getClient(cloudId: $cloudId) {
      cloudId
      name
      endDateUtc
      isDeleted
      partnerId
      managerId
      staffId
    }
  }
` as import('../../__generated__/ts-gql/GetClient').type;

export type GetClientQueryVariables = OperationVariables<
  typeof GET_CLIENT_QUERY
>;
export type GetClientQueryResult = OperationData<typeof GET_CLIENT_QUERY>;
export type GetClientItem = NonNullable<ReturnType<typeof processData>>;

export const useGetClient = (
  options: QueryHookOptions<GetClientQueryResult, GetClientQueryVariables>
) => {
  const variables = options.variables as GetClientQueryVariables;

  const res = useQuery(GET_CLIENT_QUERY, {
    context: { service: 'clients' },
    variables,
    ...options,
  });

  const { data } = res;

  return React.useMemo(() => {
    return { ...res, data: data ? processData(data) : null };
  }, [res, data]);
};

export const useGetClientAsync = () => {
  const apolloClient = useApolloClient();

  return React.useCallback(
    async (variables: GetClientQueryVariables) => {
      if (!variables) {
        throw new Error('variables are required in useGetClientAsync');
      }
      const options = {
        query: GET_CLIENT_QUERY,
        variables,
        context: { service: 'clients' },
      };
      const res = await apolloClient.query<typeof GET_CLIENT_QUERY>(options);
      const { data } = res;
      return {
        ...res,
        data: data ? processData(data) : null,
      };
    },
    [apolloClient]
  );
};

export function processData(data: GetClientQueryResult) {
  const client = data.getClient;
  const result: GetClientQueryResult['getClient'] | null = client && {
    ...client,
    endDateUtc: client?.endDateUtc && new Date(client.endDateUtc),
  };
  return result;
}
